<template>
  <div
    v-if="isPolygonControlElement"
    class="anchor"
    :style="{
      left: activePosition.x * scale + 'px',
      top: activePosition.y * scale + 'px'
    }"
  >
    <div class="controls" :style="controlPosition">
      <!-- Draw polygon -->
      <button
        v-if="shouldShowDraw"
        class="btn-float"
        title="Draw polygon"
        @click.stop="startPolygon"
      >
        <fa :icon="['fal', 'draw-polygon']" />
      </button>

      <!-- Delete polygon -->
      <button
        v-if="shouldShowDelete"
        class="btn-float"
        title="Delete polygon"
        @click.stop="deletePolygon"
      >
        <fa :icon="['fal', 'trash']" />
      </button>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";

export default {
  props: {
    scale: {
      type: Number,
      default: 1
    }
  },

  computed: {
    ...mapState("draggable", ["activeElement", "activeElementType"]),

    ...mapState({
      isDrawing: state => state.draggable.isDrawing
    }),

    isPolygonControlElement() {
      if (!this.activeElement) return false;

      if (!["marker", "tag"].includes(this.activeElementType)) return false;

      return !!this.activePosition;
    },

    activePosition() {
      if (!this.activeElement) return null;

      if (this.activeElementType === "marker" && this.activeElement.marker) {
        return this.activeElement.marker;
      }

      if (this.activeElementType === "tag" && this.activeElement.pos) {
        return this.activeElement.pos;
      }

      return null;
    },

    activeTailPosition() {
      if (!this.activeElement) return null;

      if (this.activeElementType === "marker" && this.activeElement.marker) {
        return this.activeElement.marker.tailPosition;
      }

      if (this.activeElementType === "tag") {
        return this.activeElement.tailPosition;
      }

      return null;
    },

    /** Show “draw polygon” if marker has no polygon */
    shouldShowDraw() {
      const polygon = this.activeElement && this.activeElement.polygon;
      if (!polygon || !Array.isArray(polygon.points)) return true;

      return this.isPolygonControlElement && polygon.points.length === 0;
    },

    /** Show delete if polygon exists */
    shouldShowDelete() {
      return (
        this.isPolygonControlElement &&
        this.activeElement.polygon &&
        this.activeElement.polygon.points.length > 0 &&
        !this.isDrawing
      );
    },

    controlPosition() {
      const pos = this.activeTailPosition;

      const isBottom = [
        "bottom-center",
        "bottom-left",
        "bottom-right"
      ].includes(pos);
      const isTop = ["top-center", "top-left", "top-right"].includes(pos);
      const isLeft = pos === "left-center";
      const isRight = pos === "right-center";

      if (isBottom) {
        return { top: "1rem", transform: "translate(-50%, 0%)" };
      }
      if (isTop) {
        return { bottom: "1rem", transform: "translate(-50%, 0%)" };
      }
      if (isLeft) {
        return { right: "1rem", transform: "translate(0%, -50%)" };
      }
      if (isRight) {
        return { left: "1rem", transform: "translate(0%, -50%)" };
      }

      return { transform: "translate(-120%)" };
    }
  },

  methods: {
    ensurePolygon() {
      if (!this.activeElement) return;

      if (!this.activeElement.polygon || typeof this.activeElement.polygon !== "object") {
        this.$set(this.activeElement, "polygon", {
          fill: "rgba(0,0,0,0)",
          stroke: "rgba(255,255,255,0)",
          strokeWidth: 8,
          points: [],
          dropShadow: false
        });
      }

      if (!Array.isArray(this.activeElement.polygon.points)) {
        this.$set(this.activeElement.polygon, "points", []);
      }
    },

    startPolygon() {
      if (!this.activeElement) return;
      this.ensurePolygon();
      this.$store.commit("draggable/startPolygon", this.activeElement._id);
    },

    deletePolygon() {
      if (!this.activeElement) return;
      this.$store.commit("draggable/deletePolygon", this.activeElement._id);
    }
  }
};
</script>

<style lang="scss" scoped>
.anchor {
  position: absolute;
  z-index: 100;
}

.controls {
  display: flex;
  gap: 8px;
  position: absolute;
  width: fit-content;

  .btn-float {
    appearance: none;
    aspect-ratio: 1;
    border: none;
    border-radius: 50%;
    font-size: 16px;
    white-space: nowrap;
    width: 30px;
  }
}
</style>
